<template>
    <ContentComp>
        <FormLayout
            :label-width="120"
            v-if="internalValue != null"
        >
            <FormLayoutRow>
                <div class="d-md-flex flex-fill align-items-sm-center w-md-50 align-self-start mt-1">
                    <FormLayout class="gap-0">
                        <FormLayoutRow>
                            <FormLayoutLabel>{{ $tm('race.track') }}</FormLayoutLabel>

                            <div class="d-flex flex-fill">
                                <DropdownSelect
                                    :data="availableTracksWithNull"
                                    v-model="internalValue.MapID"
                                    :maxitems="15"
                                    caption="track.selecttrack"
                                    class="flex-fill"
                                />
                                <!-- TODO: We need to implement a Participant search here so we can change the participant -->
                                <ButtonComp
                                    class="unlocked"
                                    button-type="Secondary"
                                    :title="$tm('race.addmap')"
                                    @click="openImportWizard"
                                    icon="fa-plus"
                                ></ButtonComp>
                            </div>
                        </FormLayoutRow>
                        <FormLayoutRow>
                            <FormLayoutLabel
                                text="race.overridespeed"
                                tooltip="race.overridespeedtooltip"
                            ></FormLayoutLabel>

                            <InputNullableFloat
                                v-model="internalValue.OverrideSpeed"
                                :posttext="'m/s'"
                                :icon="''"
                                >{{ $tm('race.speedms') }}</InputNullableFloat
                            >
                        </FormLayoutRow>
                        <FormLayoutRow>
                            <FormLayoutLabel>{{ $tm('race.trackcolor') }}</FormLayoutLabel>

                            <InputColor
                                v-model="internalValue.TrackColorLive!"
                                :disable-alpha="true"
                            />
                        </FormLayoutRow>
                    </FormLayout>
                </div>
                <div class="d-md-flex flex-fill align-items-sm-center w-md-50 h-100">
                    <MapComp
                        ref="mapcomp"
                        class="mt-1 ms-1 w-100 h-100"
                        :tracks="tracks"
                        :centerpoint="centerpoint"
                        :showmaplabels="true"
                        :hideDefaultControls="true"
                        :showCustomControls="true"
                        :height="450"
                        @ready="onMapReady"
                    />
                </div>
            </FormLayoutRow>
        </FormLayout>
    </ContentComp>
</template>

<script setup lang="ts">
import { ButtonComp, ContentComp, DropdownSelect, InputColor, InputNullableFloat, MapComp } from '@/components';
import { FormLayout, FormLayoutLabel, FormLayoutRow } from '@/components/Form';
import type { IMapExposed } from '@/components/Map/IMapExposed';
import { MapTrackData } from '@/components/Map/MapTrackData';
import { useDialog, useLoaders, useTaskLoader } from '@/composeables';
import { TrackDao } from '@/dao';
import type { SelectLine } from '@/objects';
import { IntArray, MapTrack, type MapPoint, type Race } from '@/objects/generated';
import { useCommonStore, useEventStoreRef, useMapstore, useMapstoreRef } from '@/stores';
import { computed, ref, watch } from 'vue';
import TrackingImportWizard from '../Tracking/TrackingImportWizard.vue';
import { useDialogBuilder } from '@/composeables/dialog/dialogbuilder';

const { eventID } = useEventStoreRef();

// The v-model
const internalValue = defineModel<Race | null>({ required: true });

const mapcomp = ref<IMapExposed | null>(null);

const { AddLoader, LoadState: SaveState } = useTaskLoader();
useLoaders(AddLoader, ['TRACKS']);

const comStore = useCommonStore();
const importedTracks = ref<MapTrack[]>([]);

const { UserAvailableTracksAsSelectline } = useMapstoreRef();
const { GetMapTrack } = useMapstore();

// The point where the focus should be centered on the map
const centerpoint = computed<MapPoint | null>(() => {
    if (mappoints.value.length > 0) {
        // If we have map points, we return the first one as the center point
        return mappoints.value[0];
    }
    return null;
});

// the list of tracks we want to show
const tracks = computed<MapTrackData[]>(() => {
    if (mappoints.value.length > 0) {
        // Create a track data object from the map points
        let mt = new MapTrackData();
        mt.MapTrack.Points = mappoints.value;
        mt.MapTrack.LineWidth = 3;
        mt.MapTrack.LineColor =
            ((internalValue.value?.TrackColorLive ?? '#00000000') == '#00000000' ? '#FF0000' : internalValue.value?.TrackColorLive) ??
            '#FF0000'; // Default to red if no color is set

        return [mt];
    }

    return [];
});

// Contains all the points for the track/map
const mappoints = ref<MapPoint[]>([]);
const reloadMapPoints = () => {
    if (!internalValue.value?.MapID) {
        mappoints.value = [];
        return;
    }

    AddLoader(async () => {
        // Since this is async we do another check on if we have the MapID
        if (!internalValue.value?.MapID) {
            mappoints.value = [];
            return;
        }
        mappoints.value = await TrackDao.GetMapPointsForTrack(eventID.value, internalValue.value?.MapID);
    }, 'tracks.loading');
};
reloadMapPoints();

const availableTracksWithNull = computed<SelectLine[]>(() => {
    // Add a null option to the available tracks
    const lines: SelectLine[] = [];

    lines.push({ value: null, text: 'track.notrack' });

    if (UserAvailableTracksAsSelectline.value) {
        lines.push(...UserAvailableTracksAsSelectline.value);
    }

    return lines;
});

/**
 * We open the import wizard dialog
 * On save, it will post the new tracks to the server
 */
const openImportWizard = async () => {
    const { LoadState: UploadLoadState, AddLoader: AddUploadLoader } = useTaskLoader();

    useDialogBuilder()
        .withTitle('trackingtracks.importwizard')
        .withBodyComponent(TrackingImportWizard, { vmodel: importedTracks })
        .withCancelButton({
            action: () => false,
        })
        .withSaveButton({
            loadstate: UploadLoadState,
            action: async () => {
                // We need to save the prepared tracks to the server
                await AddUploadLoader(async () => {
                    await comStore.EnsureLoaded(['Organizers']);

                    const added: IntArray | null = await TrackDao.PostTracksForOrganizer(importedTracks.value);

                    if (added === null || added.Values.length === 0) {
                        // If no tracks were added, we return
                        return;
                    }

                    // We refresh the list of tracks and make sure they are loaded from the server
                    for (const id of added.Values) {
                        await GetMapTrack(id);
                    }

                    internalValue.value!.MapID = added.Values[0]; // Set the first added track as the selected track
                }, 'trackingtracks.saving');
            },
        })
        .withOptions({
            Size: 'lg',
        })
        .Open();
};

/**
 * Triggered when the map is ready
 * @param state
 */
const onMapReady = (state: boolean) => {
    if (state) {
        mapcomp.value?.SetZoom('county');
    }
};

watch(
    () => internalValue.value?.MapID,
    async (newVal) => {
        await reloadMapPoints();
        if (newVal && newVal > 0) {
            // We can assume we have a track now, so we should zoom the map to the correct place
            mapcomp.value?.SetZoom('state');
        }
    },
);
</script>
